import { useState } from 'react';
import { LoadFailed } from '../components/QueryState';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { canTransferCommission } from '@promanage/core';
import {
  MAX_FIRST_INSPECTION_AFTER_DAYS,
  MAX_INSPECTION_INTERVAL_WEEKS,
  inspectionCadenceIssues,
  templateIssues,
} from '@promanage/core/inspection';
import { Button, Chip, Field, Input, Panel, PanelHeader, Select } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { SettingsNav } from '../features/settings/SettingsNav';
import { useSession } from '../features/auth/session';
import { useOrgSettings } from '../features/settings/org';
import { PlanEditor, fromRows, toRows } from '../features/inspections/PlanEditor';
import { trpc } from '../lib/trpc';
import { SaveButton, SavedNote, useUnsavedChanges } from '../components/UnsavedChanges';
import { DialogActions, FormDialog } from '../components/FormDialog';

export const Route = createFileRoute('/settings/inspections')({
  component: InspectionTemplatesPage,
});

/**
 * The agency's inspection templates — the master every property inherits from.
 *
 * ── WHY THIS IS A SETTINGS SCREEN AND NOT A PROPERTY ONE ─────────────────────────────────────
 *
 * A master template is what a hundred future inspections will ask about. Dropping "Smoke alarms"
 * from it changes what every one of them records, and nothing in the resulting reports would say
 * that a question stopped being asked — the report simply would not have the line, and a year
 * later nobody could tell whether it was never asked or always answered. That is the same class of
 * act as the owner-approval threshold in maintenance: a setting whose holders must not be the
 * people it constrains. So the server guards it with `trustAdminProcedure` and this page draws the
 * same line.
 *
 * WHAT IS NOT HERE, deliberately: anything about one house. "This property has a sleepout" is an
 * observation by whoever inspected it, and it belongs on the property's own Inspections tab where
 * the person who saw the sleepout can record it without raising a ticket.
 *
 * ── AS MANY TEMPLATES AS AN AGENCY WANTS, EXACTLY ONE DEFAULT ────────────────────────────────
 *
 * A standard house, an apartment, a full entry condition report. The default is what a booking
 * uses when nobody chooses, and there is always exactly one — the database refuses a second and
 * refuses archiving the one there is, because an agency whose default is archived has a booking
 * screen with nothing behind it.
 */
function InspectionTemplatesPage() {
  const session = useSession();
  /*
   * `trust_admin`, matching the server's `trustAdminProcedure`. `canTransferCommission` is the
   * predicate for that role — named for the first thing it guarded rather than for the role, which
   * is a wart in `access.ts` and not one to fix from here. Hidden rather than disabled: the same
   * argument `SettingsNav` makes.
   */
  const mayEdit = canTransferCommission(session?.roles ?? []);
  const [chosen, setChosen] = useState<string | null>(null);

  const templates = useQuery(
    trpc.inspections.templates.list.queryOptions(
      { includeArchived: false },
      { meta: { errorTitle: 'Could not load the inspection templates' }, staleTime: 10_000 },
    ),
  );

  /*
   * DERIVED, NOT SYNCHRONISED INTO STATE BY AN EFFECT. The default template is the one every
   * booking uses and therefore the one somebody arriving here almost always means, so it is the
   * fallback until they pick another — computed on render rather than written into state when the
   * query lands, which would be a cascading render for a value that is a pure function of two
   * things already in hand.
   */
  const selected =
    chosen ?? templates.data?.find((row) => row.isDefault)?.id ?? templates.data?.[0]?.id ?? null;

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar title="Inspections" subtitle="How often, and what every visit checks" />

      <SettingsNav />

      {templates.isError ? (
        <Panel className="p-6">
          <LoadFailed
            inline
            retry={() => void templates.refetch()}
            retrying={templates.isFetching}
          />
        </Panel>
      ) : null}

      {/*
       * LIST BESIDE EDITOR. The template being edited used to open below the whole list, so
       * switching between two meant scrolling past both; side by side, the list is the navigation.
       */}
      <div className="grid items-start gap-4 lg:grid-cols-5">
        <div className="flex flex-col gap-4 lg:col-span-2">
          <CadencePanel mayEdit={mayEdit} />
          <TemplateList
            templates={templates.data ?? []}
            selected={selected}
            onSelect={setChosen}
            mayEdit={mayEdit}
          />
        </div>

        <div className="min-w-0 lg:col-span-3">
          {selected === null ? (
            // Holds the column while there is nothing to edit, so the page does not look half-built.
            <Panel className="flex min-h-48 items-center justify-center border border-dashed border-rule p-6 text-center text-[0.8125rem] text-ink-muted shadow-none">
              A template’s rooms and items appear here.
            </Panel>
          ) : (
            <TemplateEditor
              key={selected}
              templateId={selected}
              template={templates.data?.find((row) => row.id === selected)}
              mayEdit={mayEdit}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * How often this agency inspects — the one setting that decides WHEN a hundred future visits
 * happen, next to the templates that decide what they ask.
 *
 * ── THERE IS NOTHING TO RESCHEDULE ───────────────────────────────────────────────────────────
 *
 * Due dates are worked out from these two numbers every time they are shown; no tenancy carries a
 * stored one. So saving here re-plans the entire portfolio at once — a property that was due in
 * November is due in December the moment 13 becomes 17 — and there is no job, no backfill, and no
 * tenancy left behind on the old cadence. The panel says so, because "saved" on a settings screen
 * usually means "from now on" and here it means "including the three hundred you already have".
 *
 * Except for dates already set: each tenancy carries its own due date so a property manager can
 * move it — around leave, around a tenant's shifts, to put four houses on one street into one
 * morning — and these figures do not reach back and rewrite those. The subtitle says "next visit".
 *
 * WHOLE WEEKS, so each visit falls on the same weekday as the last: a quarterly cycle booked on a
 * Wednesday stays on a Wednesday, where 90 days would drift into the weekend within a year. The
 * clock restarts from the day a routine inspection is CONDUCTED, not the day it was booked for.
 *
 * THE LINE THAT KEEPS THIS HONEST: there is no statutory inspection frequency in New Zealand; the
 * RTA constrains notice. An agency that reads a number on our settings screen as a legal
 * requirement will quote it to a tenant, so the screen that sets it carries the correction.
 */
function CadencePanel({ mayEdit }: { mayEdit: boolean }) {
  const form = useUnsavedChanges();
  const org = useOrgSettings();
  const client = useQueryClient();
  const [editing, setEditing] = useState(false);

  const cadence = org.data?.inspectionCadence ?? null;

  const save = useMutation(
    trpc.org.setInspectionCadence.mutationOptions({
      meta: { errorTitle: 'Could not change the inspection cadence' },
      onSuccess: async () => {
        form.saved();
        setEditing(false);
        await Promise.all([client.invalidateQueries({ queryKey: trpc.org.settings.queryKey() })]);
      },
    }),
  );

  return (
    <Panel className="p-0">
      <PanelHeader
        title="How often"
        subtitle="Applies from each tenancy’s next visit"
        className="px-5 pt-5 pb-2"
        aside={
          mayEdit && !editing && cadence !== null ? (
            <div className="flex items-center gap-3">
              {form.justSaved ? <SavedNote /> : null}
              <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
                Change
              </Button>
            </div>
          ) : null
        }
      />

      <div className="border-t border-rule px-5 py-4">
        {cadence === null ? (
          <p className="text-[0.8125rem] text-ink-muted">Loading…</p>
        ) : editing ? (
          <div className="contents" onChange={form.touch}>
            <CadenceForm
              cadence={cadence}
              saving={save.isPending}
              onCancel={() => {
                form.reset();
                setEditing(false);
              }}
              onSave={(next) => save.mutate(next)}
            />
          </div>
        ) : (
          <>
            <dl className="grid grid-cols-2 gap-3 rounded-md bg-surface-sunk px-4 py-3">
              <div>
                <dt className="text-[0.75rem] text-ink-muted">First visit</dt>
                <dd className="mt-0.5 text-[0.9375rem] font-semibold tabular-nums">
                  {cadence.firstAfterDays} days in
                </dd>
              </div>
              <div>
                <dt className="text-[0.75rem] text-ink-muted">Then every</dt>
                <dd className="mt-0.5 text-[0.9375rem] font-semibold tabular-nums">
                  {cadence.intervalWeeks} weeks
                </dd>
              </div>
            </dl>
            <p className="mt-3 text-[0.75rem] text-ink-muted">
              Agency policy, not law — the RTA requires 48 hours’ notice and at most one visit in
              four weeks.
            </p>
          </>
        )}
      </div>
    </Panel>
  );
}

function CadenceForm({
  cadence,
  saving,
  onCancel,
  onSave,
}: {
  cadence: { firstAfterDays: number; intervalWeeks: number };
  saving: boolean;
  onCancel: () => void;
  onSave: (next: { firstAfterDays: number; intervalWeeks: number }) => void;
}) {
  const [firstAfterDays, setFirstAfterDays] = useState(String(cadence.firstAfterDays));
  const [intervalWeeks, setIntervalWeeks] = useState(String(cadence.intervalWeeks));

  const next = {
    firstAfterDays: Number(firstAfterDays),
    intervalWeeks: Number(intervalWeeks),
  };

  // The same function the server refuses with, so the operator reads one sentence rather than
  // typing something the form accepted and the API did not.
  const issues = inspectionCadenceIssues(next);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="First visit, days in" required markOptional={false} className="w-[10rem]">
          {(props) => (
            <Input
              {...props}
              type="number"
              inputMode="numeric"
              min={0}
              max={MAX_FIRST_INSPECTION_AFTER_DAYS}
              value={firstAfterDays}
              onChange={(event) => setFirstAfterDays(event.target.value)}
            />
          )}
        </Field>

        <Field label="Then every, weeks" required markOptional={false} className="w-[10rem]">
          {(props) => (
            <Input
              {...props}
              type="number"
              inputMode="numeric"
              min={1}
              max={MAX_INSPECTION_INTERVAL_WEEKS}
              value={intervalWeeks}
              onChange={(event) => setIntervalWeeks(event.target.value)}
            />
          )}
        </Field>

        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="sm" disabled={saving || issues.length > 0} onClick={() => onSave(next)}>
            {saving ? 'Saving' : 'Save'}
          </Button>
        </div>
      </div>

      {issues.length > 0 ? (
        <ul role="alert" className="text-[0.8125rem] font-medium text-danger">
          {issues.map((issue) => (
            <li key={issue}>{issue}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

interface TemplateSummary {
  id: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  areas: number;
  items: number;
  plans: number;
}

function TemplateList({
  templates,
  selected,
  onSelect,
  mayEdit,
}: {
  templates: TemplateSummary[];
  selected: string | null;
  onSelect: (id: string) => void;
  mayEdit: boolean;
}) {
  const client = useQueryClient();
  const [name, setName] = useState('');
  /*
   * `empty`, `standard_house`, or the id of one of theirs to copy — one field, matching
   * `templateStarterSchema`, because the three are exclusive and the dropdown is where that is
   * obvious. The default is the standard house while the agency has nothing, and empty once they
   * do: the first template is the one worth having help with, and by the second they have a form
   * of their own to copy.
   */
  const [startFrom, setStartFrom] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const starter = startFrom ?? (templates.length === 0 ? 'standard_house' : 'empty');

  const refresh = () =>
    client.invalidateQueries({ queryKey: trpc.inspections.templates.list.queryKey() });

  const create = useMutation(
    trpc.inspections.templates.create.mutationOptions({
      meta: { errorTitle: 'Could not create that template' },
      onSuccess: async (created) => {
        setAdding(false);
        setName('');
        setStartFrom(null);
        onSelect(created.id);
        await refresh();
      },
    }),
  );

  const setDefault = useMutation(
    trpc.inspections.templates.setDefault.mutationOptions({
      meta: { errorTitle: 'Could not make that the default template' },
      onSuccess: refresh,
    }),
  );

  const archive = useMutation(
    trpc.inspections.templates.archive.mutationOptions({
      meta: { errorTitle: 'Could not archive that template' },
      onSuccess: refresh,
    }),
  );

  return (
    <Panel className="p-0">
      <PanelHeader
        title="Templates"
        subtitle={`${String(templates.length)} in use`}
        className="px-5 pt-5 pb-2"
        aside={
          mayEdit ? (
            <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
              <span aria-hidden="true">+</span> Add template
            </Button>
          ) : null
        }
      />

      {adding ? (
        <FormDialog
          title="Add a template"
          onClose={() => setAdding(false)}
          onSubmit={(event) => {
            event.preventDefault();
            if (name.trim().length === 0) return;
            create.mutate({ name: name.trim(), startFrom: starter });
          }}
        >
          <Field label="Name" required markOptional={false}>
            {(props) => (
              <Input
                {...props}
                value={name}
                autoFocus
                placeholder="Standard house"
                onChange={(event) => setName(event.target.value)}
              />
            )}
          </Field>

          {/*
           * THREE WAYS TO START, AND ONLY ONE OF THEM IS TYPING.
           *
           * COPYING is how an agency's second template is always made: the apartment one is the
           * house one with the garage and the lawn taken out, and building it from an empty list is
           * forty rows of retyping, which is how a template ends up half-finished.
           *
           * THE STANDARD HOUSE is how the FIRST one is made — a new agency has nothing to copy. It is
           * copied in, not linked to: from the moment it is created it is theirs, and nothing we
           * change later touches it.
           */}
          <Field label="Start from" markOptional={false}>
            {(props) => (
              <Select
                {...props}
                value={starter}
                onChange={(event) => setStartFrom(event.target.value)}
              >
                <option value="standard_house">Standard house — every room</option>
                <option value="empty">Empty</option>
                {templates.map((row) => (
                  <option key={row.id} value={row.id}>
                    Copy of {row.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <DialogActions
            pending={create.isPending}
            label="Create template"
            onClose={() => setAdding(false)}
          />
        </FormDialog>
      ) : null}

      {/*
       * FIRST RUN. An agency with no template cannot book an inspection at all, so this is not an
       * empty list to be tidied up later — it is the one thing standing between them and the
       * feature. The button fills the form in rather than creating anything: the name is a
       * suggestion, and whoever is doing this should see what they are about to get.
       */}
      {templates.length === 0 ? (
        <div className="border-t border-rule px-5 py-5">
          <p className="text-[0.875rem] font-medium text-ink">No templates yet</p>
          <p className="mt-1 text-[0.8125rem] text-ink-muted">
            Inspections need one. Start from the standard house and trim it.
          </p>
          {mayEdit ? (
            <Button
              size="sm"
              className="mt-3"
              onClick={() => {
                setName('Standard house');
                setStartFrom('standard_house');
                setAdding(true);
              }}
            >
              Start from the standard house
            </Button>
          ) : null}
        </div>
      ) : null}

      <ul
        className={[
          'divide-y divide-rule',
          templates.length > 0 ? 'border-t border-rule' : '',
        ].join(' ')}
      >
        {templates.map((template) => (
          <li
            key={template.id}
            className={[
              'flex flex-wrap items-center gap-3 px-5 py-3',
              template.id === selected ? 'bg-accent-soft/40' : '',
            ].join(' ')}
          >
            <button
              type="button"
              className="flex-1 text-left"
              onClick={() => onSelect(template.id)}
            >
              <span className="block text-[0.875rem] font-medium text-ink">{template.name}</span>
              <span className="block text-[0.75rem] text-ink-subtle">
                {template.description ??
                  `${String(template.areas)} areas · ${String(template.items)} items`}
              </span>
            </button>

            {template.isDefault ? <Chip tone="accent">Default</Chip> : null}

            {/*
             * HOW MANY PROPERTIES HAVE TAKEN A COPY. It is what makes archiving a real decision
             * rather than a tidy-up: those properties keep their plans and their history, and only
             * new bookings are affected.
             */}
            <span className="text-[0.75rem] text-ink-subtle tabular-nums">
              {template.plans} propert{template.plans === 1 ? 'y' : 'ies'}
            </span>

            {mayEdit && !template.isDefault ? (
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={setDefault.isPending}
                  onClick={() => setDefault.mutate({ templateId: template.id })}
                >
                  Make default
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={archive.isPending}
                  onClick={() => archive.mutate({ templateId: template.id })}
                >
                  Archive
                </Button>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </Panel>
  );
}

/**
 * The editor's data has to be LOADED BEFORE THE FORM EXISTS.
 *
 * A form initialised empty and then filled in by an effect is a cascading render and, worse, a
 * moment during which an operator can type into a field that is about to be overwritten. So the
 * query lives out here and the editor below takes its initial tree as a prop, keyed on the
 * template so switching between two of them remounts rather than merging one into the other.
 */
function TemplateEditor({
  templateId,
  template,
  mayEdit,
}: {
  templateId: string;
  template: TemplateSummary | undefined;
  mayEdit: boolean;
}) {
  const tree = useQuery(
    trpc.inspections.templates.get.queryOptions(
      { templateId },
      { meta: { errorTitle: 'Could not load that template' } },
    ),
  );

  if (tree.isPending) {
    return (
      <Panel className="p-5">
        <p className="text-[0.8125rem] text-ink-muted">Loading…</p>
      </Panel>
    );
  }

  if (tree.isError || tree.data === undefined) {
    return (
      <Panel className="p-5">
        <LoadFailed inline retry={() => void tree.refetch()} retrying={tree.isFetching} />
      </Panel>
    );
  }

  return (
    <TemplateForm
      key={templateId}
      templateId={templateId}
      initialName={template?.name ?? ''}
      initialAreas={tree.data}
      mayEdit={mayEdit}
    />
  );
}

function TemplateForm({
  templateId,
  initialName,
  initialAreas,
  mayEdit,
}: {
  templateId: string;
  initialName: string;
  initialAreas: ReadonlyArray<{
    id: string;
    name: string;
    guidance: string | null;
    items: ReadonlyArray<{ id: string; name: string; guidance: string | null }>;
  }>;
  mayEdit: boolean;
}) {
  const form = useUnsavedChanges();
  const client = useQueryClient();
  const [name, setName] = useState(initialName);
  const [rows, setRows] = useState(() =>
    toRows(initialAreas.map((area) => ({ ...area, items: [...area.items] }))),
  );

  const save = useMutation(
    trpc.inspections.templates.save.mutationOptions({
      meta: { errorTitle: 'Could not save that template' },
      onSuccess: async () => {
        form.saved();
        await Promise.all([
          client.invalidateQueries({ queryKey: trpc.inspections.templates.list.queryKey() }),
          client.invalidateQueries({
            queryKey: trpc.inspections.templates.get.queryKey({ templateId }),
          }),
        ]);
      },
    }),
  );

  const areas = fromRows(rows);
  // The same function the server runs. This one is the courtesy — it puts the duplicate-name
  // sentence under the field rather than after a round trip — and the server's is the gate.
  const issues = templateIssues({ name, areas });

  return (
    <div className="flex flex-col gap-3" onChange={form.touch}>
      <Panel className="flex flex-wrap items-end gap-3 p-4">
        {/* Required — the label said "optional", which is `Field`'s default and was wrong here. */}
        <Field label="Template name" required markOptional={false} className="min-w-[14rem] flex-1">
          {(props) => (
            <Input
              {...props}
              value={name}
              disabled={!mayEdit}
              onChange={(event) => setName(event.target.value)}
            />
          )}
        </Field>

        <div className="flex items-center gap-2">
          <SaveButton
            type="button"
            pending={save.isPending}
            saved={form.justSaved}
            disabled={!mayEdit || issues.length > 0}
            onClick={() => save.mutate({ templateId, name: name.trim(), areas })}
          >
            Save
          </SaveButton>
        </div>

        {issues.length > 0 ? (
          <ul role="alert" className="w-full text-[0.8125rem] font-medium text-danger">
            {issues.map((issue) => (
              <li key={issue}>{issue}</li>
            ))}
          </ul>
        ) : null}

        {/* Properties already inspected keep their own plans until somebody resets one — the
            master is the starting point, not a live copy — so "Saved" is all there is to say. */}
      </Panel>

      <PlanEditor
        rows={rows}
        onChange={(next) => {
          form.touch();
          setRows(next);
        }}
        disabled={!mayEdit}
      />
    </div>
  );
}
